import { useState, type Ref } from 'react'
import { gramsToKgInput, parseKgToGrams, sanitizeKgInput, type Grams } from '@/lib/weight'
import { useI18n } from '@/i18n'
import { Input, type InputProps } from './Input'

export interface KgInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'type' | 'inputMode' | 'defaultValue'
> {
  /** Integer grams, or null when empty / incomplete. */
  value: Grams | null
  onChange: (grams: Grams | null) => void
  /** Digits allowed before the decimal point. Default 5 (99,999.999 kg). */
  maxWholeDigits?: number
  ref?: Ref<HTMLInputElement>
}

/**
 * Kilograms with exactly 3 decimals (gram precision). Opens the numeric keypad, keeps what the
 * person typed (so "20.0" stays "20.0" while typing) and reports integer grams — never a float.
 */
export function KgInput({
  value,
  onChange,
  maxWholeDigits = 5,
  size = 'touch',
  placeholder = '0.000',
  onBlur,
  ...rest
}: KgInputProps) {
  const { t } = useI18n()
  const [text, setText] = useState(() => (value === null ? '' : gramsToKgInput(value)))
  const [prevValue, setPrevValue] = useState(value)

  // Follow external changes (e.g. a reset) without clobbering what the person is typing:
  // adjust state during render when the prop changes (no effect round-trip).
  if (value !== prevValue) {
    setPrevValue(value)
    const parsed = parseKgToGrams(text)
    if (value === null ? parsed !== null : parsed !== value) {
      setText(value === null ? '' : gramsToKgInput(value))
    }
  }

  return (
    <Input
      {...rest}
      size={size}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      enterKeyHint="done"
      pattern="[0-9]*[.,]?[0-9]{0,3}"
      placeholder={placeholder}
      className="font-mono tabular"
      trailing={<span className="font-mono">{t('field.kg')}</span>}
      value={text}
      onChange={(e) => {
        const next = sanitizeKgInput(e.target.value, maxWholeDigits)
        setText(next)
        onChange(parseKgToGrams(next))
      }}
      onBlur={(e) => {
        const g = parseKgToGrams(text)
        if (g !== null) setText(gramsToKgInput(g)) // normalise "20.5" → "20.500"
        onBlur?.(e)
      }}
    />
  )
}

export interface IntegerInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'type' | 'inputMode'
> {
  value: number | null
  onChange: (n: number | null) => void
  maxDigits?: number
  ref?: Ref<HTMLInputElement>
}

/** Whole numbers (bag counts, slip numbers) with the numeric keypad. */
export function IntegerInput({ value, onChange, maxDigits = 6, ...rest }: IntegerInputProps) {
  return (
    <Input
      {...rest}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="off"
      className="font-mono tabular"
      value={value === null ? '' : String(value)}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, maxDigits)
        onChange(digits === '' ? null : Number(digits))
      }}
    />
  )
}
